import { useRef } from "react";
import { createPortal } from "react-dom";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { X, Printer, Share2 } from "lucide-react";
import { NeuButton } from "../../../components/neu/Neu";
import { printQrZone } from "./printQrZone";

// نافذة عرض/طباعة رمز QR: كانفاس مخفي يُنتج PNG موثوق للطباعة والمشاركة (يعمل على iOS الأصلي عبر Web Share).
export const QrPrintModal = ({ zone, branchName, onClose }) => {
  const { t } = useTranslation();
  const wrap = useRef(null);
  const canvas = () => wrap.current?.querySelector("canvas");

  const print = () => {
    const png = canvas()?.toDataURL("image/png");
    if (png) printQrZone(zone, branchName, t, png);
  };

  const save = () => {
    const c = canvas();
    if (!c) return;
    c.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], `QR-${zone.code}.png`, { type: "image/png" });
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: zone.name });
          return;
        }
      } catch {
        /* المستخدم ألغى المشاركة */
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    }, "image/png");
  };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4" data-testid="qr-print-modal">
      <div className="relative w-full max-w-sm rounded-[28px] bg-background p-5 shadow-neu"
        style={{ paddingTop: "max(env(safe-area-inset-top),1.25rem)" }}>
        <button type="button" data-testid="qr-print-close" onClick={onClose} aria-label={t("app.close")}
          className="absolute end-4 top-4 flex h-10 w-10 items-center justify-center rounded-full shadow-neu-sm active:shadow-neu-in">
          <X className="h-5 w-5" />
        </button>
        <p className="pe-10 text-base font-black" data-testid="qr-print-title">{zone.name}</p>
        {zone.floor ? <p className="text-xs text-muted-foreground">{t("qr.floor")}: {zone.floor}</p> : null}
        <div className="mx-auto mt-4 w-fit rounded-2xl bg-white p-4 shadow-neu-in">
          <QRCodeSVG value={zone.qr_payload} size={256} level="M" />
        </div>
        <div ref={wrap} className="hidden" aria-hidden="true">
          <QRCodeCanvas value={zone.qr_payload} size={512} level="M" marginSize={2} />
        </div>
        <p className="mt-3 text-center font-mono text-sm tracking-[4px] text-muted-foreground" dir="ltr">{zone.code}</p>
        <div className="mt-5 grid grid-cols-2 gap-2">
          <NeuButton type="button" variant="primary" data-testid="qr-print-go" onClick={print}>
            <Printer className="mr-1 h-4 w-4" /> {t("qr.print")}
          </NeuButton>
          <NeuButton type="button" data-testid="qr-download-png" onClick={save}>
            <Share2 className="mr-1 h-4 w-4" /> {t("qr.download")}
          </NeuButton>
        </div>
        <NeuButton type="button" data-testid="qr-print-close-bottom" onClick={onClose} className="mt-2 w-full py-3">
          {t("app.close")}
        </NeuButton>
      </div>
    </div>,
    document.body,
  );
};
